/**
 * Hook d'authentification Native SSO v1.0
 * Architecture Frontend-First avec appels directs à l'IAM
 * 
 * @version 2.7.9
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { nativeAuthService } from '../services/nativeAuth';
import { getNativeStorage, setNativeAuthConfig, setNativeStorage, STORAGE_KEYS, isDebugMode, repairNativeSsoStorage, getSsoSessionSnapshot, getNativeAuthConfig, type NativeStorageAdapter } from '../services/api';
import { useTokenHealthCheck } from './useTokenHealthCheck';
import type {
  NativeAuthState,
  NativeAuthStatus,
  NativeExchangeResponse,
  AccountType,
  NativeUser,
  UserInfos,
} from '../types/native';

const FORCE_NEEDS_ACCESS_MODAL = true;

function normalizeNativeUser(user: NativeUser | UserInfos | null | undefined): NativeUser | null {
  if (!user) return null;
  const input = user as Partial<NativeUser> & Partial<UserInfos>;

  return {
    reference: input.reference ?? input.alias_reference ?? '',
    name: input.name ?? '',
    email: input.email ?? undefined,
    phone: input.phone,
    ccphone: input.ccphone,
    image_url: input.image_url ?? input.image,
    town: input.town,
    country: input.country,
    address: input.address,
    auth_2fa: input.auth_2fa,
    alias_reference: input.alias_reference,
  };
}

/** Helper: persist session via the package storage adapter (unified keys) */
function saveSession(exchangeResult: NativeExchangeResponse, accountType: AccountType | 'user' | 'client') {
  const storage = getNativeStorage();
  const sanctumToken = exchangeResult.iam_token;
  storage.setItem(STORAGE_KEYS.AUTH_TOKEN, sanctumToken);
  if (exchangeResult.expires_at) {
    storage.setItem(STORAGE_KEYS.TOKEN_EXPIRES_AT, exchangeResult.expires_at);
  }
  if (exchangeResult.refresh_expires_at) {
    storage.setItem(STORAGE_KEYS.REFRESH_EXPIRES_AT, exchangeResult.refresh_expires_at);
  }

  const aliasRef = exchangeResult.alias_reference || '';
  const userToStore = {
    ...exchangeResult.user_infos,
    reference: exchangeResult.reference,
    alias_reference: aliasRef,
  };
  
  // Store alias_reference separately for quick access
  if (aliasRef) {
    storage.setItem(STORAGE_KEYS.ALIAS_REFERENCE, aliasRef);
  }
  
  storage.setItem(STORAGE_KEYS.USER, JSON.stringify(userToStore));
  storage.setItem(STORAGE_KEYS.REGISTRATION_MODE, typeof accountType === 'string' ? accountType : 'user');
  
  return { token: sanctumToken, user: userToStore };
}

/** Helper: clear session via the package storage adapter */
function clearSession() {
  const storage = getNativeStorage();
  storage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
  storage.removeItem(STORAGE_KEYS.TOKEN);
  storage.removeItem(STORAGE_KEYS.USER);
  storage.removeItem(STORAGE_KEYS.REGISTRATION_MODE);
  storage.removeItem(STORAGE_KEYS.ALIAS_REFERENCE);
  storage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  storage.removeItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
  storage.removeItem(STORAGE_KEYS.REFRESH_EXPIRES_AT);
}

function getErrorMessage(err: unknown, context: string): { message: string; type: string } {
  if (err instanceof Error) {
    if (err.message.includes('fetch') || err.message.includes('network')) {
      return { message: 'Vérifiez votre connexion Internet', type: 'network' };
    }
    if (err.message.includes('timeout')) {
      return { message: 'Le serveur met trop de temps à répondre', type: 'timeout' };
    }
    return { message: err.message, type: 'unknown' };
  }
  return { message: `Erreur lors de ${context}`, type: 'unknown' };
}

export interface UseNativeAuthOptions {
  /** URL du Backend SaaS */
  saasApiUrl: string;
  /** URL du Backend IAM */
  iamApiUrl: string;
  /** Charger les credentials automatiquement au montage */
  autoLoadCredentials?: boolean;
  /** Type de compte par défaut à persister (défaut: 'user') */
  defaultAccountType?: 'user' | 'client';
  /** Préfixe local utilisé pour sélectionner les credentials côté SaaS */
  configPrefix?: string;
  /** Stockage injecté pour les apps natives / Capacitor */
  storage?: NativeStorageAdapter;
}

export function useNativeAuth(options: UseNativeAuthOptions) {
  const { saasApiUrl, iamApiUrl, autoLoadCredentials = true, defaultAccountType = 'user', configPrefix = 'iam', storage } = options;

  if (storage) {
    setNativeStorage(storage);
  }
  
  const configuredRef = useRef(false);

  useEffect(() => {
    const repairResult = repairNativeSsoStorage();
    if (repairResult.cleaned && isDebugMode()) {
      console.warn('🔧 [useNativeAuth] Storage SSO réparé', repairResult.reason);
    }
  }, []);
  
  // Debug réactif : mis à jour après loadCredentials
  const [isDebug, setIsDebug] = useState(isDebugMode());
  
  useEffect(() => {
    if (!configuredRef.current) {
      setNativeAuthConfig({ saasApiUrl, iamApiUrl, configPrefix, storage });
      configuredRef.current = true;
    }
  }, [saasApiUrl, iamApiUrl, configPrefix, storage]);

  const [state, setState] = useState<NativeAuthState & { otpMethod?: string | null; otpSentTo?: string | null }>({
    credentialsLoaded: false,
    processToken: null,
    status: null,
    user: null,
    application: null,
    prompt: null,
    alternativeMethod: null,
    conflict: null,
    loading: false,
    error: null,
    errorType: null,
    otpMethod: null,
    otpSentTo: null,
  });

  const [accountType, setAccountType] = useState<AccountType>('email');

  // --- Token Health Check ---
  const handleTokenInvalid = useCallback(() => {
    if (isDebug) console.log('🔐 [HealthCheck] Token invalide — déconnexion locale');
    clearSession();
    setState({
      credentialsLoaded: false,
      processToken: null,
      status: null,
      user: null,
      application: null,
      prompt: null,
      alternativeMethod: null,
      conflict: null,
      loading: false,
      error: null,
      errorType: null,
      otpMethod: null,
      otpSentTo: null,
    });
  }, [isDebug]);

  const handleUserUpdated = useCallback((userInfos: UserInfos) => {
    // Merge fresh user_infos into stored user
    const storedRaw = getNativeStorage().getItem(STORAGE_KEYS.USER);
    if (storedRaw) {
      try {
        const stored = JSON.parse(storedRaw);
        const merged = normalizeNativeUser({ ...stored, ...userInfos });
        if (!merged) return;
        getNativeStorage().setItem(STORAGE_KEYS.USER, JSON.stringify(merged));
        setState(prev => ({ ...prev, user: merged }));
      } catch { /* ignore */ }
    }
  }, []);

  const refreshingRef = useRef(false);
  const tryRefreshSession = useCallback(async (): Promise<'recovered' | 'invalid' | 'noop'> => {
    // Avoid parallel refreshes
    if (refreshingRef.current) return 'noop';
    refreshingRef.current = true;
    try {
      const refreshToken = getNativeStorage().getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        if (isDebug) console.log('🔐 [Refresh] Pas de refresh_token — impossible de recuperer');
        return 'noop';
      }

      // Offline: never logout and don't attempt refresh
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        if (isDebug) console.log('🔐 [Refresh] Offline — skip');
        return 'noop';
      }

      const res = await nativeAuthService.refresh();
      if (res.success) {
        // Update in-memory state from the session snapshot
        const snapshot = getSsoSessionSnapshot<UserInfos>();
        const user = normalizeNativeUser(snapshot.user);
        if (user) {
          setState(prev => ({ ...prev, user, status: 'completed' }));
        }
        return 'recovered';
      }

      // If backend explicitly rejects refresh, treat as invalid session
      if (res.success === false && res.error_type === 'invalid_refresh') {
        if (isDebug) console.log('🔐 [Refresh] invalid_refresh — deconnexion autorisee');
        return 'invalid';
      }

      return 'noop';
    } catch (err) {
      // Network/timeout/5xx => keep session
      if (isDebug) console.log('🔐 [Refresh] Echec refresh (non-bloquant)');
      return 'noop';
    } finally {
      refreshingRef.current = false;
    }
  }, [isDebug]);

  useTokenHealthCheck({
    enabled: state.status === 'completed' && state.user !== null,
    saasApiUrl,
    iamApiUrl,
    onTokenInvalid: handleTokenInvalid,
    onUnauthorized: tryRefreshSession,
    onUserUpdated: handleUserUpdated,
    debug: isDebug,
  });

  // Proactive refresh based on expires_at (best-effort)
  useEffect(() => {
    if (state.status !== 'completed' || !state.user) return;

    const expiresAt = getNativeStorage().getItem(STORAGE_KEYS.TOKEN_EXPIRES_AT);
    if (!expiresAt) return;

    const ts = Date.parse(expiresAt);
    if (Number.isNaN(ts)) return;

    // Refresh 5 minutes before expiry (server time skew tolerant)
    const marginMs = 5 * 60 * 1000;
    const delay = Math.max(ts - Date.now() - marginMs, 30 * 1000); // at least 30s

    const t = setTimeout(() => {
      tryRefreshSession();
    }, delay);

    return () => clearTimeout(t);
  }, [state.status, state.user, tryRefreshSession]);

  // Check existing session
  useEffect(() => {
    const snapshot = getSsoSessionSnapshot<UserInfos>();
    const user = normalizeNativeUser(snapshot.user);
    if (snapshot.authToken && user) {
      setState(prev => ({ ...prev, user, status: 'completed' }));
    }
  }, []);

  // Background auto-refresh of credentials before TTL expiration
  useEffect(() => {
    // Only refresh while user is NOT authenticated and credentials are loaded
    if (state.status === 'completed' || !state.credentialsLoaded) return;

    const ttl = nativeAuthService.getCredentialsTtl();
    const refreshInterval = Math.max((ttl - 30) * 1000, 30000); // At least 30s

    if (isDebug) {
      console.log(`🔄 [Native] Auto-refresh credentials every ${refreshInterval / 1000}s (TTL: ${ttl}s)`);
    }

    const timer = setInterval(async () => {
      try {
        await nativeAuthService.loadCredentials();
        // Re-check debug après refresh (le backend peut changer la valeur)
        setIsDebug(isDebugMode());
        if (isDebugMode()) console.log('✅ [Native] Credentials auto-refreshed');
      } catch (err) {
        if (isDebugMode()) console.warn('⚠️ [Native] Auto-refresh failed:', err);
      }
    }, refreshInterval);

    return () => clearInterval(timer);
  }, [state.status, state.credentialsLoaded, isDebug]);

  const loadCredentials = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      await nativeAuthService.loadCredentials();
      // Mettre à jour le debug réactif après chargement des credentials
      setIsDebug(isDebugMode());
      setState(prev => ({ ...prev, credentialsLoaded: true, loading: false }));
      return { success: true };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'le chargement');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, []);

  // Auto-load credentials
  useEffect(() => {
    if (autoLoadCredentials && !state.credentialsLoaded && !state.user) {
      loadCredentials();
    }
  }, [autoLoadCredentials, state.credentialsLoaded, state.user, loadCredentials]);

  const loginWithEmail = useCallback(async (email: string) => {
    setState(prev => ({ ...prev, loading: true, error: null, errorType: null }));
    try {
      if (!nativeAuthService.hasCredentials()) {
        await nativeAuthService.loadCredentials();
      }
      const response = await nativeAuthService.loginEmail(email);

      // Garde: ancien backend peut retourner needs_access directement depuis init
      if (response.success && (response.status === 'needs_access' || response.needs_access)) {
        const bypassResult = FORCE_NEEDS_ACCESS_MODAL ? null : await autoResolveNeedsAccess(response);
        if (bypassResult) {
          return { success: true, status: 'completed', callback_token: bypassResult.callback_token };
        }

        setState(prev => ({
          ...prev,
          processToken: response.process_token || null,
          status: 'needs_access' as NativeAuthStatus,
          application: response.application || null,
          prompt: response.prompt || null,
          otpMethod: response.otp_method || prev.otpMethod,
          otpSentTo: response.otp_sent_to || prev.otpSentTo,
          user: response.user ? {
            reference: `USR-${response.user.id}`,
            name: response.user.name,
            email: response.user.email,
          } : null,
          loading: false,
        }));
        return { success: true, status: 'needs_access' };
      }

      if (response.success && response.process_token) {
        setState(prev => ({
          ...prev,
          processToken: response.process_token!,
          status: response.status as NativeAuthStatus,
          application: response.application || null,
          prompt: response.prompt || null,
          otpMethod: response.otp_method || prev.otpMethod,
          otpSentTo: response.otp_sent_to || prev.otpSentTo,
          user: response.user ? {
            reference: `USR-${response.user.id}`,
            name: response.user.name,
            email: response.user.email,
          } : null,
          loading: false,
        }));
        return { success: true, status: response.status };
      }

      if (response.error_type === 'alias_disabled' && response.alternative_method) {
        setState(prev => ({
          ...prev,
          alternativeMethod: {
            type: response.alternative_method!,
            value: response.alternative_method === 'email' 
              ? response.alternative_email! 
              : response.alternative_phone!,
          },
          loading: false,
          error: 'Cette méthode de connexion est désactivée',
          errorType: 'alias_disabled',
        }));
        return { success: false, error_type: 'alias_disabled' };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Erreur d\'authentification',
        errorType: response.error_type || 'unknown',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'l\'authentification');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, []);

  const loginWithPhone = useCallback(async (ccphone: string, phone: string) => {
    setState(prev => ({ ...prev, loading: true, error: null, errorType: null }));
    try {
      if (!nativeAuthService.hasCredentials()) {
        await nativeAuthService.loadCredentials();
      }
      const response = await nativeAuthService.loginPhone(ccphone, phone);

      // Garde: ancien backend peut retourner needs_access directement depuis init
      if (response.success && (response.status === 'needs_access' || response.needs_access)) {
        const bypassResult = FORCE_NEEDS_ACCESS_MODAL ? null : await autoResolveNeedsAccess(response);
        if (bypassResult) {
          return { success: true, status: 'completed', callback_token: bypassResult.callback_token };
        }

        setState(prev => ({
          ...prev,
          processToken: response.process_token || null,
          status: 'needs_access' as NativeAuthStatus,
          application: response.application || null,
          otpMethod: response.otp_method || prev.otpMethod,
          otpSentTo: response.otp_sent_to || prev.otpSentTo,
          user: response.user ? {
            reference: `USR-${response.user.id}`,
            name: response.user.name,
            email: response.user.email,
          } : null,
          loading: false,
        }));
        return { success: true, status: 'needs_access' };
      }

      if (response.success && response.process_token) {
        setState(prev => ({
          ...prev,
          processToken: response.process_token!,
          status: response.status as NativeAuthStatus,
          application: response.application || null,
          otpMethod: response.otp_method || prev.otpMethod,
          otpSentTo: response.otp_sent_to || prev.otpSentTo,
          loading: false,
        }));
        return { success: true, status: response.status };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Erreur d\'authentification',
        errorType: response.error_type || 'unknown',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'l\'authentification');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, []);

  const loginWithAccessOtp = useCallback(async (otpCode: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      if (!nativeAuthService.hasCredentials()) {
        await nativeAuthService.loadCredentials();
      }
      const response = await nativeAuthService.loginAccessOtp(otpCode);

      // v4.5: Handle needs_access (user needs to confirm access)
      // Tolère success:true ou success:false avec needs_access
      if ((response.needs_access || response.status === 'needs_access') && response.process_token) {
        const bypassResult = FORCE_NEEDS_ACCESS_MODAL ? null : await autoResolveNeedsAccess(response);
        if (bypassResult) {
          return { success: true, status: 'completed', callback_token: bypassResult.callback_token };
        }

        setState(prev => ({
          ...prev,
          processToken: response.process_token!,
          status: 'needs_access' as NativeAuthStatus,
          application: response.application || null,
          user: response.user ? {
            reference: `USR-${response.user.id}`,
            name: response.user.name,
            email: response.user.email,
          } : null,
          loading: false,
        }));
        return { success: true, status: 'needs_access' };
      }

      // v4.5: Handle direct callback_token (user already has access)
      if (response.success && response.callback_token) {
        setState(prev => ({ ...prev, loading: false }));
        return { success: true, status: 'completed', callback_token: response.callback_token };
      }

      // Legacy: Handle process_token response
      if (response.success && response.process_token) {
        setState(prev => ({
          ...prev,
          processToken: response.process_token!,
          status: response.status as NativeAuthStatus,
          loading: false,
        }));
        return { success: true, status: response.status };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Code invalide',
        errorType: response.error_type || 'invalid_otp',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'l\'authentification');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, []);

  const register = useCallback(async (
    data: {
      name: string;
      email?: string;
      ccphone: string;
      phone: string;
      town?: string;
      country?: string;
    }
  ) => {
    setState(prev => ({ ...prev, loading: true, error: null, errorType: null }));
    try {
      if (!nativeAuthService.hasCredentials()) {
        await nativeAuthService.loadCredentials();
      }
      const registrationType = accountType === 'phone-only' ? 'phone' : 'email';
      
      const response = await nativeAuthService.register({
        ...data,
        email: registrationType === 'phone' ? '' : data.email,
        registration_type: registrationType,
      });

      if (response.success && response.process_token) {
        setState(prev => ({
          ...prev,
          processToken: response.process_token!,
          status: response.status as NativeAuthStatus,
          otpMethod: response.otp_method || null,
          otpSentTo: response.otp_sent_to || null,
          conflict: response.conflict ? {
            type: response.conflict.type,
            maskedIdentifier: response.conflict.masked_identifier,
            options: {
              canLogin: response.conflict.options.can_login,
              canRecoverByEmail: response.conflict.options.can_recover_by_email,
              canRecoverBySms: response.conflict.options.can_recover_by_sms,
              maskedEmail: response.conflict.options.masked_email,
              maskedPhone: response.conflict.options.masked_phone,
            },
          } : null,
          loading: false,
        }));
        return { success: true, status: response.status };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Erreur lors de l\'inscription',
        errorType: response.error_type || 'unknown',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'l\'inscription');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, [accountType]);

  const submitPassword = useCallback(async (password: string) => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await nativeAuthService.validate(state.processToken, { password });

      if (response.success && response.callback_token) {
        const exchangeResult = await nativeAuthService.exchange(response.callback_token);
        if (exchangeResult.success) {
          const { user: savedUser } = saveSession(exchangeResult, defaultAccountType);
          setState(prev => ({
            ...prev,
            status: 'completed',
            user: savedUser,
            processToken: null,
            loading: false,
          }));
          return { success: true, user: savedUser, callback_token: response.callback_token };
        }
        setState(prev => ({
          ...prev,
          loading: false,
          error: exchangeResult.message || 'Erreur lors de l\'échange',
          errorType: 'exchange_failed',
        }));
        return { success: false, error_type: 'exchange_failed' };
      }

      if (response.requires_2fa) {
        setState(prev => ({ ...prev, status: 'pending_2fa', loading: false }));
        return { success: true, requires_2fa: true };
      }

      // needs_access after password validation — stocker le nouveau process_token
      if (response.success && (response.status === 'needs_access' || response.needs_access)) {
        const bypassResult = FORCE_NEEDS_ACCESS_MODAL ? null : await autoResolveNeedsAccess(response);
        if (bypassResult) {
          return { success: true, user: bypassResult.user, callback_token: bypassResult.callback_token };
        }

        setState(prev => ({
          ...prev,
          status: 'needs_access' as NativeAuthStatus,
          processToken: response.process_token || prev.processToken,
          application: response.application || prev.application,
          user: response.user ? {
            reference: `USR-${response.user.id}`,
            name: response.user.name,
            email: response.user.email,
          } : prev.user,
          prompt: response.prompt || prev.prompt,
          loading: false,
        }));
        return { success: true, status: 'needs_access' };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Mot de passe incorrect',
        errorType: response.error_type || 'invalid_password',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'la validation');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, [state.processToken, defaultAccountType]);

  const submitOtp = useCallback(async (otpCode: string) => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await nativeAuthService.validate(state.processToken, { otp_code: otpCode });

      if (response.success && response.callback_token) {
        const exchangeResult = await nativeAuthService.exchange(response.callback_token);
        if (exchangeResult.success) {
          const { user: savedUser } = saveSession(exchangeResult, defaultAccountType);
          setState(prev => ({
            ...prev,
            status: 'completed',
            user: savedUser,
            processToken: null,
            loading: false,
          }));
          return { success: true, user: savedUser, callback_token: response.callback_token };
        }
        setState(prev => ({
          ...prev,
          loading: false,
          error: exchangeResult.message || 'Erreur lors de l\'échange',
          errorType: 'exchange_failed',
        }));
        return { success: false, error_type: 'exchange_failed' };
      }

      if (response.success && response.status === 'pending_registration') {
        setState(prev => ({ ...prev, status: 'pending_registration', loading: false }));
        return { success: true, status: 'pending_registration' };
      }

      // needs_access after OTP validation — stocker le nouveau process_token
      if (response.success && (response.status === 'needs_access' || response.needs_access)) {
        const bypassResult = FORCE_NEEDS_ACCESS_MODAL ? null : await autoResolveNeedsAccess(response);
        if (bypassResult) {
          return { success: true, user: bypassResult.user, callback_token: bypassResult.callback_token };
        }

        setState(prev => ({
          ...prev,
          status: 'needs_access' as NativeAuthStatus,
          processToken: response.process_token || prev.processToken,
          application: response.application || prev.application,
          user: response.user ? {
            reference: `USR-${response.user.id}`,
            name: response.user.name,
            email: response.user.email,
          } : prev.user,
          prompt: response.prompt || prev.prompt,
          loading: false,
        }));
        return { success: true, status: 'needs_access' };
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Code incorrect',
        errorType: response.error_type || 'invalid_otp',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'la vérification');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, [state.processToken, defaultAccountType]);

  const submitTotp = useCallback(async (totpCode: string) => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await nativeAuthService.verifyTotp(state.processToken, totpCode);

      if (response.success && response.callback_token) {
        const exchangeResult = await nativeAuthService.exchange(response.callback_token);
        if (exchangeResult.success) {
          const { user: savedUser } = saveSession(exchangeResult, defaultAccountType);
          setState(prev => ({
            ...prev,
            status: 'completed',
            user: savedUser,
            processToken: null,
            loading: false,
          }));
          return { success: true, user: savedUser };
        }
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Code 2FA incorrect',
        errorType: response.error_type || 'invalid_totp',
      }));
      return { success: false, error_type: response.error_type };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'la vérification 2FA');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, [state.processToken, defaultAccountType]);

  const grantAccess = useCallback(async () => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await nativeAuthService.grantAccess(state.processToken);

      if (response.success && response.callback_token) {
        const exchangeResult = await nativeAuthService.exchange(response.callback_token);
        if (exchangeResult.success) {
          const { user: savedUser } = saveSession(exchangeResult, defaultAccountType);
          setState(prev => ({
            ...prev,
            status: 'completed',
            user: savedUser,
            processToken: null,
            loading: false,
          }));
          return { success: true, user: savedUser, callback_token: response.callback_token };
        }
      }

      setState(prev => ({
        ...prev,
        loading: false,
        error: response.message || 'Erreur lors de l\'accès',
        errorType: response.error_type || 'unknown',
      }));
      return { success: false };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'l\'accès');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false, error_type: type };
    }
  }, [state.processToken, defaultAccountType]);

  const resendOtp = useCallback(async () => {
    if (!state.processToken) {
      return { success: false, error: 'Session invalide' };
    }
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await nativeAuthService.resendOtp(state.processToken);
      setState(prev => ({
        ...prev,
        loading: false,
        otpMethod: response.otp_method || prev.otpMethod,
        otpSentTo: response.otp_sent_to || prev.otpSentTo,
      }));
      return { 
        success: response.success, 
        cooldown: response.cooldown_remaining,
        message: response.message,
        otp_code_dev: response.otp_code_dev,
        otp_method: response.otp_method,
        otp_sent_to: response.otp_sent_to,
      };
    } catch (err) {
      const { message, type } = getErrorMessage(err, 'le renvoi');
      setState(prev => ({ ...prev, loading: false, error: message, errorType: type }));
      return { success: false };
    }
  }, [state.processToken]);

  const setSession = useCallback((data: NativeExchangeResponse) => {
    const { user: savedUser } = saveSession(data, defaultAccountType);
    setState(prev => ({
      ...prev,
      user: savedUser,
      status: 'completed',
      processToken: null,
    }));
  }, [defaultAccountType]);

  const logout = useCallback(async () => {
    const storage = getNativeStorage();
    const token = storage.getItem(STORAGE_KEYS.AUTH_TOKEN) || storage.getItem(STORAGE_KEYS.TOKEN);
    try {
      if (token) {
        await nativeAuthService.logout(token);
      }
    } catch { /* ignore */ }

    clearSession();
    
    setState({
      credentialsLoaded: false,
      processToken: null,
      status: null,
      user: null,
      application: null,
      prompt: null,
      alternativeMethod: null,
      conflict: null,
      loading: false,
      error: null,
      errorType: null,
      otpMethod: null,
      otpSentTo: null,
    });
  }, []);

  const reset = useCallback(() => {
    setState(prev => ({
      ...prev,
      processToken: null,
      status: null,
      application: null,
      prompt: null,
      alternativeMethod: null,
      conflict: null,
      error: null,
      errorType: null,
    }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null, errorType: null }));
  }, []);

  const autoResolveNeedsAccess = useCallback(async (
    response: {
      process_token?: string | null;
      application?: NativeAuthState['application'];
      prompt?: NativeAuthState['prompt'];
      user?: { id: number; name: string; email?: string | null; image_url?: string | null };
    }
  ): Promise<{ success: true; user: NativeUser; callback_token: string } | null> => {
    if (!getNativeAuthConfig().bypass || !response.process_token) {
      return null;
    }

    if (isDebug) {
      console.log('⏩ [Native] bypass=true — auto grant access');
    }

    const grantResponse = await nativeAuthService.grantAccess(response.process_token);
    if (!grantResponse.success || !grantResponse.callback_token) {
      return null;
    }

    const exchangeResult = await nativeAuthService.exchange(grantResponse.callback_token);
    if (!exchangeResult.success) {
      return null;
    }

    const { user: savedUser } = saveSession(exchangeResult, defaultAccountType);
    setState(prev => ({
      ...prev,
      status: 'completed',
      user: savedUser,
      processToken: null,
      application: response.application || prev.application,
      prompt: response.prompt || prev.prompt,
      loading: false,
    }));

    return { success: true, user: savedUser, callback_token: grantResponse.callback_token };
  }, [defaultAccountType, isDebug]);

  return {
    // State
    credentialsLoaded: state.credentialsLoaded,
    processToken: state.processToken,
    status: state.status,
    user: state.user,
    application: state.application,
    prompt: state.prompt,
    alternativeMethod: state.alternativeMethod,
    conflict: state.conflict,
    loading: state.loading,
    error: state.error,
    errorType: state.errorType,
    isAuthenticated: state.status === 'completed' && state.user !== null,
    
    /** Debug réactif — mis à jour après loadCredentials */
    isDebug,
    
    accountType,
    isPhoneOnly: accountType === 'phone-only',
    otpMethod: state.otpMethod,
    otpSentTo: state.otpSentTo,

    // Methods
    loadCredentials,
    loginWithEmail,
    loginWithPhone,
    loginWithAccessOtp,
    submitPassword,
    submitOtp,
    submitTotp,
    grantAccess,
    resendOtp,
    setSession,
    logout,
    reset,
    clearError,
    register,
    setAccountType,
  };
}

export default useNativeAuth;
